import { _ } from "../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import keyFigureComponentCache from "../../models/keyFigure/keyFigureComponentCache.js";
import session from "../../core/session.js";
import userFactory from "../../models/userFactory.js";
import UserSettingsTabDocumentSection from "../../core/documentSections/UserSettingsTabDocumentSection.js";

/**
 * `mySettingsDocument` is a document used to set settings of the
 * current user such as the language and the avatar.
 */
export default accountingDocument.subclass((that, my) => {
	const currentUser = session.getCurrentUser();
	that.documentId = "my-settings";

	const antiForgeryToken = session.getAntiForgeryToken();

	my.currentUserRepository = makeMonitorRepository({
		route: "/Shared/CurrentUser",
		antiForgeryToken,
	});

	my.keyFigureService = getKeyFigureService();

	my.avatarRepository = makeMonitorRepository({
		route: "/Shared/Avatar/User/#userId",
		model: userFactory,
		antiForgeryToken,
	});

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.editedUser = currentUser;

		my.tabs = new UserSettingsTabDocumentSection({
			currentUser,
			editedUser: my.editedUser,
			updatePhoneCallback: my.updatePhone,
			updateLanguageCallback: my.updateLanguage,
			avatarChosenCallback: my.uploadAvatar,
			avatarDeletedCallback: my.deleteAvatar,
			showBasicInfoTab: !my.editedUserIsTemporary(),
			showAvatarChooserTab: !my.editedUserIsTemporary(),
			showAAAUserEditionButtons: my.isManageUsersInUiEnabled(),
		});
	};

	that.title = _("{name}'s settings", {
		name: currentUser.getName(),
	});

	that.hasHelp = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return false;
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	my.renderContentOn = function (html) {
		html.render(my.section({ content: my.tabs }));
	};

	my.clearKeyFigureCache = function () {
		keyFigureComponentCache.flush();

		return my.keyFigureService.populateCache();
	};

	my.updateLanguage = async function (languageCode) {
		await Promise.all([
			session.setLanguage(languageCode),
			my.currentUserRepository.update({
				model: { language: languageCode },
				successMessage: null,
			}),
		]);

		my.clearKeyFigureCache();
		my.success(_("The language is successfully updated"));
		that.update();
	};

	my.updatePhone = async function (user) {
		await my.currentUserRepository.update({
			model: user,
			successMessage: _("Phone successfully updated"),
		});

		that.update();
	};

	my.isManageUsersInUiEnabled = function () {
		return session.isManageUsersInUiEnabled;
	};

	my.editedUserIsTemporary = function () {
		return my.editedUser?.isTemporaryUser();
	};

	my.uploadAvatar = async function (user, dataString) {
		await my.avatarRepository.update({
			filter: {
				userId: user.getId(),
			},
			model: { picture: dataString },
			readResponse: false,
			successMessage: _("Avatar was successfully updated"),
		});

		currentUser.useStoredPictureUrl();
		my.tabs.setEditedUser(currentUser, []);
	};

	my.deleteAvatar = async function (user) {
		await my.avatarRepository.remove({
			filter: {
				userId: user.getId(),
			},
			successMessage: _("Avatar was successfully deleted"),
		});

		currentUser.useDefaultPictureUrl();
		my.tabs.setEditedUser(currentUser, []);
	};
});
